import { getFocusableComponents } from 'helpers/element';
import { useEffect, useRef } from 'react';

const KEYBOARD = {
    PAGE_UP: 'PageUp',
    PAGE_DOWN: 'PageDown',
    CTRL_START: 'Home',
    CTRL_END: 'End',
};

export const useFeedKeyboardInteractions = () => {
    const ref = useRef<HTMLElement>(null);
    const getPreviousArticle = (currentIndex: number, articles: NodeListOf<HTMLElement>) => (
        articles[currentIndex > 0 ? currentIndex - 1 : articles.length - 1]
    );
    const getNextArticle = (currentIndex: number, articles: NodeListOf<HTMLElement>) => (
        articles[currentIndex < articles.length - 1 ? currentIndex + 1 : 0]
    );

    useEffect(() => {
        const handleKeyDown = (event: { code: string; preventDefault: () => void }) => {
            const isPageUp = event.code === KEYBOARD.PAGE_UP;
            const isPageDown = event.code === KEYBOARD.PAGE_DOWN;
            const isStart = event.code === KEYBOARD.CTRL_START;
            const isEnd = event.code === KEYBOARD.CTRL_END;
            const feedElement = document.activeElement && document.activeElement.closest('[role="feed"]');

            if (feedElement === null) { return; }
            const articles = feedElement.querySelectorAll<HTMLElement>('[role="article"]');

            if (!articles.length) { return; }

            if (isPageUp || isPageDown) {
                event.preventDefault();
                const indexOfActiveArticle = Array.from(articles).indexOf(
                    document.activeElement as HTMLElement,
                );

                if (isPageUp) {
                    getPreviousArticle(indexOfActiveArticle, articles).focus();
                }

                if (isPageDown) {
                    getNextArticle(indexOfActiveArticle, articles).focus();
                }
            }

            if (isStart) {
                const previousElement = feedElement.previousSibling;
                const focusableElements = getFocusableComponents(previousElement);
                const previousFocusableElement = focusableElements[focusableElements.length - 1];

                previousFocusableElement.focus();
            }

            if (isEnd) {
                const followingElement = feedElement.nextSibling;
                const focusableElements = getFocusableComponents(followingElement);
                const nextFocusableElement = focusableElements[0];

                if (nextFocusableElement.disabled) {
                    const focusableSibling = getFocusableComponents(nextFocusableElement.nextElementSibling);
                    focusableSibling?.[0].focus();
                } else {
                    nextFocusableElement.focus();
                }
            }
        };

        const element = ref.current;

        if (element) { element.addEventListener('keydown', handleKeyDown); }

        return () => {
            if (element) { element.removeEventListener('keydown', handleKeyDown); }
        };
    }, []);

    return ref;
};
